/**
 * Centralized WhatsApp conversion funnel helper.
 *
 * All WhatsApp buttons in the app build their href via `buildWhatsAppUrl`
 * so we can change the phone number, message templates, or tracking in one
 * place.
 */
import { trackWhatsAppClick as gaTrackWhatsAppClick } from './analytics';

export const WHATSAPP_NUMBER = '61481865742';

// Variant → message template. Each variant supports optional context
// (subject, level, category) that is interpolated into the template.
const TEMPLATES = {
  default: () =>
    `Hello, I'm interested in Winhouse Tutors.\n\n` +
    `I am a:\n` +
    `[ ] Student\n` +
    `[ ] Parent\n` +
    `[ ] Tutor\n\n` +
    `Subject/Area:\n__________\n\n` +
    `Please assist me with next steps.`,

  find_tutor: ({ subject = '', level = '' } = {}) =>
    `Hello, I am looking for a tutor.\n\n` +
    `Subject: ${subject || '__________'}\n` +
    `Level: ${level || '__________'}\n\n` +
    `Please help me get started.`,

  become_tutor: () =>
    `Hello, I would like to join Winhouse Tutors as a tutor.\n\n` +
    `Subjects: __________\n` +
    `Experience: __________\n\n` +
    `Please guide me through the next steps.`,

  resources: ({ category = '' } = {}) =>
    `Hello, I am using your study resources and would like additional help.\n\n` +
    `Category: ${category || '__________'}\n\n` +
    `Please assist me.`,
};

/**
 * Build a fully-qualified wa.me URL for the requested variant + context.
 *
 * @param {Object} opts
 * @param {'default'|'find_tutor'|'become_tutor'|'resources'} [opts.variant]
 * @param {string} [opts.subject]   Auto-filled for find_tutor
 * @param {string} [opts.level]     Auto-filled for find_tutor
 * @param {string} [opts.category]  Auto-filled for resources
 * @returns {string} https://wa.me/<number>?text=<encoded>
 */
export const buildWhatsAppUrl = ({
  variant = 'default',
  subject,
  level,
  category,
} = {}) => {
  const tpl = TEMPLATES[variant] || TEMPLATES.default;
  const text = tpl({ subject, level, category });
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
};

/**
 * Lightweight click tracking. Sends a fire-and-forget POST to the backend
 * AND a GA4 `whatsapp_click` event. Never throws — tracking failures must
 * never block the user's WhatsApp flow.
 */
export const trackWhatsAppClick = (source, variant = 'default') => {
  // GA4 side (see /app/frontend/src/lib/analytics.js).
  try {
    gaTrackWhatsAppClick(source, variant);
  } catch {
    /* noop */
  }
  try {
    const BACKEND_URL =
      process.env.REACT_APP_BACKEND_URL || window.location.origin;
    // keepalive=true ensures the request goes out even if the page unloads
    // after the click.
    fetch(`${BACKEND_URL}/api/v4/analytics/whatsapp-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: source || 'unknown',
        variant,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      }),
      keepalive: true,
    }).catch(() => {
      /* swallow — analytics must never break the UX */
    });
  } catch {
    /* noop */
  }
};
